import { useState, FormEvent, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useEvent } from '../store';
import { useAuth } from '../store';
import { saveEvent, isUltraAdmin } from '../utils';
import { saveTemplate } from '../services/firestore';
import { parseOddsText } from '../utils/oddsParser';
import type { Fighter, FightMarket, Event } from '../types';

/**
 * Edit Fight Page
 * Admin page for editing fight odds manually
 * ONLY WORKS FOR TEMPLATES - Ultra-admin only
 */
export function EditFightPage() {
  const { eventId, fightId } = useParams<{ eventId: string; fightId: string }>();
  const navigate = useNavigate();
  const { event, getFight, updateFight } = useEvent();
  const { currentUser } = useAuth();
  
  const [fighter1Odds, setFighter1Odds] = useState('');
  const [fighter2Odds, setFighter2Odds] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showOddsParser, setShowOddsParser] = useState(false);
  const [pastedOddsText, setPastedOddsText] = useState('');
  const [parseMode, setParseMode] = useState<'add' | 'replace'>('replace');
  const [editableMarkets, setEditableMarkets] = useState<FightMarket[]>([]);

  // Block access for non-templates or non-ultra-admins
  if (event && !event.isTemplate) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <h2 className="text-xl font-bold text-primary-text mb-4">Access Denied</h2>
          <p className="text-secondary-text mb-4">
            Fight odds cannot be edited on event instances. They are managed through the template.
          </p>
          <Button onClick={() => navigate(`/event/${eventId}`)}>
            Back to Event
          </Button>
        </Card>
      </div>
    );
  }

  if (currentUser && !isUltraAdmin(currentUser)) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <h2 className="text-xl font-bold text-primary-text mb-4">Ultra Admin Only</h2>
          <p className="text-secondary-text mb-4">
            Only ultra-admins can manage template fights.
          </p>
          <Button onClick={() => navigate('/games')}>
            Back to Games
          </Button>
        </Card>
      </div>
    );
  }

  // Helper to save event or template depending on type
  const saveEventOrTemplate = async (eventData: Event) => {
    if (eventData.isTemplate) {
      await saveTemplate(eventData);
    } else {
      await saveEvent(eventData);
    }
  };

  useEffect(() => {
    if (!eventId || !fightId) {
      navigate(`/admin/${eventId}`);
      return;
    }

    const fight = getFight(fightId);
    if (fight) {
      setFighter1Odds(fight.fighters[0]?.odds.toString() || '');
      setFighter2Odds(fight.fighters[1]?.odds.toString() || '');
      setEditableMarkets(fight.markets || []);
    }
    setLoading(false);
  }, [eventId, fightId, getFight, navigate]);

  const handleMarketOutcomeOddsChange = (marketIndex: number, outcomeIndex: number, newOdds: string) => {
    const updatedMarkets = [...editableMarkets];
    const oddsValue = parseFloat(newOdds);
    if (!isNaN(oddsValue)) {
      updatedMarkets[marketIndex].outcomes[outcomeIndex].odds = oddsValue;
      setEditableMarkets(updatedMarkets);
    }
  };

  const handleDeleteMarket = (marketIndex: number) => {
    const confirmation = window.confirm('Are you sure you want to delete this prop bet market?');
    if (confirmation) {
      const updatedMarkets = editableMarkets.filter((_, idx) => idx !== marketIndex);
      setEditableMarkets(updatedMarkets);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading...</p>
        </div>
      </div>
    );
  }

  if (!fightId) {
    navigate(`/admin/${eventId}`);
    return null;
  }

  const fight = getFight(fightId);
  if (!fight) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary-text mb-4">
              Fight Not Found
            </h1>
            <Button variant="secondary" onClick={() => navigate(`/admin/${eventId}`)}>
              Back to Admin Panel
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleParseOdds = async () => {
    if (!pastedOddsText.trim()) {
      setError('Please paste odds data');
      return;
    }

    const parsed = parseOddsText(pastedOddsText);
    if (!parsed) {
      setError('Failed to parse odds data. Please check format and try again.');
      return;
    }

    // Update moneyline odds
    setFighter1Odds(parsed.fighter1Odds.toString());
    setFighter2Odds(parsed.fighter2Odds.toString());

    if (!event || !fight) {
      setError('Fight not found');
      return;
    }

    try {
      let updatedMarkets: FightMarket[];
      
      if (parseMode === 'replace') {
        // Replace all markets with new ones
        updatedMarkets = parsed.markets;
      } else {
        // Add new markets to existing ones
        const existingMarkets = fight.markets || [];
        updatedMarkets = [...existingMarkets, ...parsed.markets];
      }

      // Update fighters with new odds
      const updatedFighters: Fighter[] = fight.fighters.map((fighter, index) => ({
        ...fighter,
        previousOdds: fighter.odds,
        odds: index === 0 ? parsed.fighter1Odds : parsed.fighter2Odds,
      }));

      // Update fight with new odds and markets
      updateFight(fightId, {
        fighters: updatedFighters,
        markets: updatedMarkets,
        oddsLastUpdated: Date.now(),
      });

      // Save to Firestore
      const updatedEvent = {
        ...event,
        fights: event.fights.map((f) =>
          f.id === fightId
            ? { ...f, fighters: updatedFighters, markets: updatedMarkets, oddsLastUpdated: Date.now() }
            : f
        ),
      };
      await saveEventOrTemplate(updatedEvent);

      setShowOddsParser(false);
      setPastedOddsText('');
      setError('');
    } catch (err) {
      setError('Failed to update fight with parsed odds');
      console.error('Error updating fight:', err);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const odds1 = parseFloat(fighter1Odds);
    const odds2 = parseFloat(fighter2Odds);

    if (isNaN(odds1)) {
      setError('Fighter 1 odds must be a valid number');
      return;
    }

    if (isNaN(odds2)) {
      setError('Fighter 2 odds must be a valid number');
      return;
    }

    if (!event) {
      setError('Event not found');
      return;
    }

    try {
      // Update fighters with new odds
      // IMPORTANT: This only updates the fight odds, all existing user bets are preserved
      const updatedFighters: Fighter[] = fight.fighters.map((fighter, index) => ({
        ...fighter,
        previousOdds: fighter.odds,
        odds: index === 0 ? odds1 : odds2,
      }));

      // Update fight with new odds and markets (existing bets in participants array are NOT modified)
      updateFight(fightId, {
        fighters: updatedFighters,
        markets: editableMarkets,
        oddsLastUpdated: Date.now(),
      });

      // Save to Firestore - only updates fight odds and markets, bets remain unchanged
      const updatedEvent = {
        ...event,
        fights: event.fights.map((f) =>
          f.id === fightId
            ? { ...f, fighters: updatedFighters, markets: editableMarkets, oddsLastUpdated: Date.now() }
            : f
        ),
        // participants array (which contains bets) is not modified here
      };
      await saveEventOrTemplate(updatedEvent);

      // Navigate back to admin panel
      navigate(`/admin/${eventId}`);
    } catch (err) {
      setError('Failed to update odds. Please try again.');
      console.error('Error updating odds:', err);
    }
  };

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-primary-text">Edit Odds</h1>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/admin/${eventId}`)}
          >
            Cancel
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Odds Parser Section */}
        <Card padding="lg">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-primary-text">Parse Odds Data</h2>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowOddsParser(!showOddsParser)}
            >
              {showOddsParser ? 'Hide' : 'Show'} Parser
            </Button>
          </div>

          {showOddsParser && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-text mb-2">
                  Paste Odds Data
                </label>
                <textarea
                  value={pastedOddsText}
                  onChange={(e) => setPastedOddsText(e.target.value)}
                  className="w-full px-4 py-3 bg-secondary-bg border border-border rounded text-primary-text focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                  rows={8}
                  placeholder="Paste odds data here (e.g., from DraftKings, BetMGM, etc.)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-text mb-2">
                  Parse Mode
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={parseMode === 'add'}
                      onChange={() => setParseMode('add')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-primary-text">
                      Add Markets <span className="text-sm text-secondary-text">(keep existing)</span>
                    </span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={parseMode === 'replace'}
                      onChange={() => setParseMode('replace')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-primary-text">
                      Replace All <span className="text-sm text-secondary-text">(overwrite)</span>
                    </span>
                  </label>
                </div>
              </div>

              <Button
                variant="primary"
                fullWidth
                onClick={handleParseOdds}
              >
                Parse & Update Odds
              </Button>
            </div>
          )}
        </Card>

        {/* Manual Odds Edit Section */}
        <Card padding="lg">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-primary-text mb-2">
              {fight.fighters[0]?.name || 'TBD'} vs {fight.fighters[1]?.name || 'TBD'}
            </h2>
            <p className="text-sm text-secondary-text">
              Update moneyline odds manually
            </p>
            <div className="mt-3 p-3 bg-info/10 border border-info rounded-lg">
              <p className="text-sm text-info">
                ℹ️ <strong>Note:</strong> Updating odds will only affect new bets. All existing bets placed by users will be preserved with their original odds.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-primary-text mb-3">
                {fight.fighters[0]?.name || 'Fighter 1'} Odds
              </label>
              <Input
                type="number"
                step="any"
                placeholder="e.g., -150 or +200"
                value={fighter1Odds}
                onChange={(e) => setFighter1Odds(e.target.value)}
                error={error && error.includes('Fighter 1') ? error : ''}
                helperText="American odds format (e.g., -150, +200)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-text mb-3">
                {fight.fighters[1]?.name || 'Fighter 2'} Odds
              </label>
              <Input
                type="number"
                step="any"
                placeholder="e.g., -150 or +200"
                value={fighter2Odds}
                onChange={(e) => setFighter2Odds(e.target.value)}
                error={error && error.includes('Fighter 2') ? error : ''}
                helperText="American odds format (e.g., -150, +200)"
              />
            </div>

            {/* Prop Bets / Markets Section */}
            {editableMarkets.length > 0 && (
              <div className="pt-6 border-t border-border">
                <h3 className="text-lg font-semibold text-primary-text mb-4">
                  Prop Bets / Additional Markets
                </h3>
                <div className="space-y-6">
                  {editableMarkets.map((market, marketIdx) => (
                    <div key={marketIdx} className="p-4 bg-tertiary-bg rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-semibold text-primary-text">{market.name}</h4>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteMarket(marketIdx)}
                        >
                          Delete Market
                        </Button>
                      </div>
                      <div className="space-y-3">
                        {market.outcomes.map((outcome, outcomeIdx) => (
                          <div key={outcomeIdx} className="flex items-center gap-3">
                            <div className="flex-1">
                              <label className="block text-sm text-secondary-text mb-1">
                                {outcome.name}
                              </label>
                            </div>
                            <div className="w-32">
                              <Input
                                type="number"
                                step="any"
                                placeholder="Odds"
                                value={outcome.odds.toString()}
                                onChange={(e) => handleMarketOutcomeOddsChange(marketIdx, outcomeIdx, e.target.value)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && !error.includes('Fighter') && (
              <div className="p-3 bg-error/10 border border-error rounded-lg">
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={() => navigate(`/admin/${eventId}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                fullWidth
              >
                Update Odds
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}



